/**
 * `ai_readout` — the executive readout block (epic v3).
 *
 * Every other block on a scorecard is SQL: a number, a share, a rank.
 * This one is PROSE, and prose is the single thing a fact table cannot
 * produce. Its only possible source is the report's AI-analysis
 * endpoint, which is why this block does not follow the chart pipeline
 * (config → SQL → rows → renderer) and fetches on its own.
 *
 * Three rules, all of them about not lying:
 *
 *   1. **The AI marker is not optional.** The body always renders
 *      inside a `Callout` with `aiGenerated`, so a reader can never
 *      mistake generated narrative for a measured number sitting in
 *      the same grid.
 *   2. **A failure is visible.** No silent empty card: a fetch error
 *      shows the error and a retry, exactly like every other block
 *      (`BlockErrorCard`). Absent prose and failed prose are different
 *      facts.
 *   3. **A host that cannot serve it says so.** `fetchAiReadout` is
 *      optional on the data source; the builder preview has no such
 *      endpoint, and an honest note beats an empty box that reads as
 *      "the AI had nothing to say".
 *
 * Refetches when the filters change — the readout describes the slice
 * the reader is looking at, and stale prose next to fresh numbers is
 * the worst failure this block could have.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Callout, Spinner } from "@pervasivemind/bi-components";

import { BlockErrorCard } from "./BlockStatus";
import type { AiReadout, DashboardDataSource } from "./dashboard-data";
import { useLang } from "./i18n-binding";
import { MarkedText } from "./MarkedText";

interface AiReadoutCardProps {
  dataSource: DashboardDataSource;
  activeFilters: Record<string, string>;
  title?: string;
  /** ``callout`` (default) draws the dashboard's tinted Callout with the
   *  AI-generated marker. ``bare`` renders ONLY the prose, for a host
   *  that already provides card chrome and the marker — the Home board
   *  tile (Pablo, 2026-09-02: a callout inside a card read as a box in a
   *  box). The fetch, the stale marker and the error card are identical
   *  in both. */
  variant?: "callout" | "bare";
}

export function AiReadoutCard({
  dataSource,
  activeFilters,
  title,
  variant = "callout",
}: AiReadoutCardProps): JSX.Element {
  const { t } = useTranslation();
  const lang = useLang();
  const [data, setData] = useState<AiReadout | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Generation guard: a slow response for an old filter set must never
  // overwrite a newer one (same rule as the dashboard's other fetches).
  const genRef = useRef(0);
  // `activeFilters` is a fresh object every render; the SIGNATURE is
  // what actually changed. Keying the effect on the object would
  // refetch the LLM on every parent render — expensive and wrong.
  const filterSig = useMemo(
    () =>
      Object.entries(activeFilters)
        .filter(([, v]) => v != null && v !== "")
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${v}`)
        .join("&"),
    [activeFilters],
  );

  const fetcher = dataSource.fetchAiReadout;

  const load = useCallback(() => {
    if (!fetcher) return;
    const gen = ++genRef.current;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetcher({ activeFilters, language: lang, signal: controller.signal })
      .then((res) => {
        if (gen !== genRef.current) return;
        setData(res);
      })
      .catch((err: unknown) => {
        if (gen !== genRef.current) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (gen === genRef.current) setLoading(false);
      });
    return () => controller.abort();
  }, [fetcher, activeFilters, lang]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
    // `filterSig` is the intentional dependency: `activeFilters` is a
    // fresh object every render and would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSig, fetcher, lang]);

  if (!fetcher) {
    return (
      <Callout
        tone="neutral"
        title={title}
        className="h-full"
      >
        {t("dashboard.chart.readoutUnavailable", {
          defaultValue: "The narrative readout is generated in the portal",
        })}
      </Callout>
    );
  }

  if (error && !data) {
    return <BlockErrorCard message={error} onRetry={load} />;
  }

  const insights = data?.insights ?? [];

  const loadingLine = (
    <div className="flex items-center gap-2 py-2 text-xs text-neutral-500">
      <Spinner size={16} ariaLabel={t("common.loading", { defaultValue: "Loading" })} />
      {t("dashboard.chart.readoutLoading", { defaultValue: "Generating the readout…" })}
    </div>
  );

  const body = (
    <>
      {loading && !data ? (
        loadingLine
      ) : (
        <div className="space-y-2" aria-busy={loading || undefined}>
          {/* Refetch with prose on screen: mark it as NOT current — the
              spinner line on top + dimmed prose, until the new slice lands. */}
          {loading && loadingLine}
          <div className={loading ? "space-y-2 opacity-60" : "space-y-2"}>
          {/* Through the structure marks (PR1): paragraphs, bullets and
              the bold-labelled fields read as such, never as literal
              asterisks. */}
          {data?.executive_summary && (
            <MarkedText
              text={data.executive_summary}
              className="text-sm leading-snug text-neutral-800"
            />
          )}
          {/* Numbered like the reference, but NOT a `RankedList`: that
              component styles its subtitle as a caption
              (`text-xs text-neutral-600` since the 2026-07-23 contrast
              sweep — was neutral-400 at ~2.2:1 on this panel's
              lavender). Caption styling fits a note under a ranked row;
              not the sentences that ARE this block's content. Prose
              inherits the Callout body (`text-sm text-neutral-700`,
              ~9:1) and the numeral takes the panel's own accent. */}
          {insights.length > 0 && (
            <ol className="space-y-1.5" aria-label={title ?? "Readout"}>
              {insights.map((insight, i) => (
                <li key={`insight-${i}`} className="flex gap-2.5">
                  <span className="w-4 shrink-0 text-sm font-bold tabular-nums text-primary-ink">
                    {i + 1}
                  </span>
                  {/* Title on its own line: the three headlines have to
                      scan as a summary by themselves, without reading
                      the supporting sentence under each one. */}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-neutral-800">
                      {insight.title}
                    </div>
                    {insight.summary && (
                      <MarkedText text={insight.summary} className="leading-snug" />
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
          </div>
          {/* A refresh that failed while prose from a previous slice is
              still on screen: keep the prose but MARK it, never let
              stale narrative pass as current. */}
          {error && (
            <p className="text-[11px] font-semibold text-danger-600">
              {t("dashboard.chart.readoutStale", {
                defaultValue: "Showing the previous readout — refresh failed",
              })}
            </p>
          )}
        </div>
      )}
    </>
  );

  if (variant === "bare") {
    return (
      <div
        data-testid="ai-readout-bare"
        className="h-full overflow-y-auto px-4 py-3 text-sm leading-relaxed text-neutral-700"
      >
        {body}
      </div>
    );
  }

  return (
    <Callout
      accent="info"
      title={title}
      aiGenerated
      // Spelled out, not the bare "AI" pill the other AI surfaces use.
      // This block is a full-width band of prose sitting in a grid of
      // measured numbers; a two-letter grey chip is easy to read as a
      // chart-type label or skip entirely. "AI-generated" cannot be
      // misread, and that is the whole point of the marker.
      aiLabel={t("dashboard.chart.aiGeneratedLong", {
        defaultValue: "AI-generated",
      })}
      className="h-full overflow-y-auto"
    >
      {body}
    </Callout>
  );
}
